import { Stack } from "@mui/material";
import { SurveyRuleAction } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleAction";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  DropdownInputField,
  DropdownItem,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React from "react";
import { useTranslation } from "react-i18next";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import { SurveyRuleTargetModel } from "../../models/SurveyRuleTargetModel";
import { useSurveyRuleStore } from "../../stores/Hooks";
import { getActionValidationErrorHelperText } from "../../utils/getSurveyRuleValidationErrorHelperText";
import { SurveyRuleTargetInputField } from "../inputs/SurveyRuleTargetInputField";

interface SurveyRuleActionFormProps {
  surveyRuleId: string;
  surveyAction: SurveyRuleAction.SurveyRuleAction | null;
  surveyTarget: Instance<typeof SurveyRuleTargetModel>[];
}

const ACTION_FIELD_WIDTH = "132px";

export const SurveyRuleActionForm: React.FC<SurveyRuleActionFormProps> =
  observer(({ surveyRuleId, surveyAction, surveyTarget }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();

    const surveyRuleStore = useSurveyRuleStore();
    const surveyRule = surveyRuleStore.getNonNullRule(surveyRuleId);
    const hasRespondentCondition = Array.from(
      surveyRule.conditions.values(),
    ).some((condition) => condition.keyType.type === "RESPONDENT");

    const actionOptions: DropdownItem[] = [
      {
        name: t("surveys.rules.skipToAction"),
        value: SurveyRuleAction.SurveyRuleAction.SKIP_TO,
      },
      {
        name: t("surveys.rules.showTargetAction"),
        value: SurveyRuleAction.SurveyRuleAction.SHOW_TARGET,
      },
      {
        name: t("surveys.rules.hideTargetAction"),
        value: SurveyRuleAction.SurveyRuleAction.HIDE_TARGET,
      },
    ].filter(
      (actionOption) =>
        !hasRespondentCondition ||
        actionOption.value !== SurveyRuleAction.SurveyRuleAction.SKIP_TO,
    );
    const actionHelperText = getActionValidationErrorHelperText(
      t,
      surveyRule.actionValidationErrors,
    );

    const isSurveyRuleAction = (
      value: string,
    ): value is SurveyRuleAction.SurveyRuleAction =>
      Object.values(SurveyRuleAction.SurveyRuleAction).includes(
        value as SurveyRuleAction.SurveyRuleAction,
      );

    const handleActionChange = (selectedItem: DropdownItem): void => {
      if (!isSurveyRuleAction(selectedItem.value)) {
        throw new Error(
          `Invalid survey rule action value: ${selectedItem.value}`,
        );
      }

      surveyRuleStore.setAction(surveyRuleId, selectedItem.value);
    };

    return (
      <Stack
        direction="row"
        gap={spacing.spaceXS}
        divider={<DividerComponent orientation="vertical" height="100%" />}
        alignItems="start"
      >
        <DropdownInputField
          name="surveyRuleAction"
          label={EMPTY_CHARACTER}
          placeholder={t("surveys.rules.action")}
          value={surveyAction ?? undefined}
          options={actionOptions}
          onSelect={handleActionChange}
          error={surveyRule.actionValidationErrors.length > 0}
          helperText={actionHelperText}
          width={ACTION_FIELD_WIDTH}
          minWidth={ACTION_FIELD_WIDTH}
        />
        <SurveyRuleTargetInputField
          surveyAction={surveyAction}
          surveyRuleId={surveyRuleId}
          surveyTarget={surveyTarget}
        />
      </Stack>
    );
  });
